/**
 * Acquires the recording stream by routing all audio through an AudioContext mixer.
 *
 * Every recording — even solo mic — goes through the mixer. This enables:
 * - Mid-recording mic device switching (Phase 3b) via swapMicSource
 * - Telehealth remote audio mixing (Phase 3c) via connectRemoteTrack
 *
 * BackgroundThrottleGuard note: Chrome may throttle AudioContext destination
 * tracks in background tabs. Empirical testing needed (Phase 3b) to determine
 * if the guard's RTCPeerConnection loopback must use the raw mic stream instead.
 */

import SentryService from '@services/SentryService';

export interface AudioMixerResult {
  stream: MediaStream;
  cleanup: () => void;
  sharedMediaActive: boolean;
  swapMicSource: (newMicStream: MediaStream) => void;
  connectRemoteTrack: (participantId: string, track: MediaStreamTrack) => void;
  disconnectRemoteTrack: (participantId: string) => void;
  audioContext: AudioContext | null;
}

/** @deprecated Use AudioMixerResult */
export type MixedStreamResult = AudioMixerResult;

export async function acquireRecordingStream(
  micStream: MediaStream,
  options: { useSharedMedia: boolean; preferCurrentTab: boolean },
): Promise<AudioMixerResult> {
  // ── Fallback: if AudioContext fails, return raw mic with no-op methods ──
  let audioContext: AudioContext;
  try {
    audioContext = new AudioContext();
    if (audioContext.state === 'suspended') {
      await audioContext.resume().catch(() => {});
    }
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'acquireRecordingStream' },
      extra: { reason: 'AudioContext constructor failed' },
    });
    return {
      stream: micStream,
      cleanup: () => {},
      sharedMediaActive: false,
      swapMicSource: () => {},
      connectRemoteTrack: () => {},
      disconnectRemoteTrack: () => {},
      audioContext: null,
    };
  }

  // ── Always-on mixer ──
  const destination = audioContext.createMediaStreamDestination();
  let micSourceNode = audioContext.createMediaStreamSource(micStream);
  micSourceNode.connect(destination);

  const remoteNodes = new Map<string, MediaStreamAudioSourceNode>();
  let sharedMediaSource: MediaStreamAudioSourceNode | null = null;
  let sharedMediaStream: MediaStream | null = null;

  // ── Shared media (screen/tab audio) ──
  let sharedMediaActive = false;

  if (options.useSharedMedia) {
    try {
      const sharedMedia = await navigator.mediaDevices.getDisplayMedia({
        preferCurrentTab: options.preferCurrentTab,
        selfBrowserSurface: 'include',
        audio: true,
        video: true,
      } as DisplayMediaStreamOptions);

      if (sharedMedia.getAudioTracks().length === 0) {
        sharedMedia.getTracks().forEach(t => t.stop());
      } else {
        sharedMedia.getVideoTracks().forEach(t => t.stop());
        sharedMediaStream = sharedMedia;
        sharedMediaSource = audioContext.createMediaStreamSource(sharedMedia);
        sharedMediaSource.connect(destination);
        sharedMediaActive = true;
      }
    } catch {
      // User cancelled the display picker — continue with mic only
    }
  }

  // ── Methods ──

  function swapMicSource(newMicStream: MediaStream): void {
    micSourceNode.disconnect();
    micSourceNode = audioContext.createMediaStreamSource(newMicStream);
    micSourceNode.connect(destination);
  }

  function connectRemoteTrack(participantId: string, track: MediaStreamTrack): void {
    if (track.readyState === 'ended') {
      SentryService.addBreadcrumb({
        category: 'recording.mixer',
        message: `Skipped ended remote track: ${participantId}`,
        level: 'warning',
      });
      return;
    }

    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }

    const existing = remoteNodes.get(participantId);
    if (existing) {
      existing.disconnect();
    }

    try {
      const source = audioContext.createMediaStreamSource(new MediaStream([track]));
      source.connect(destination);
      remoteNodes.set(participantId, source);
      SentryService.addBreadcrumb({
        category: 'recording.mixer',
        message: `Connected remote track: ${participantId}`,
        level: 'info',
        data: { readyState: track.readyState, muted: track.muted },
      });
    } catch (error) {
      SentryService.addBreadcrumb({
        category: 'recording.mixer',
        message: `Failed to connect remote track: ${participantId}`,
        level: 'error',
        data: { error: String(error) },
      });
    }
  }

  function disconnectRemoteTrack(participantId: string): void {
    const existing = remoteNodes.get(participantId);
    if (existing) {
      existing.disconnect();
      remoteNodes.delete(participantId);
      SentryService.addBreadcrumb({
        category: 'recording.mixer',
        message: `Disconnected remote track: ${participantId}`,
        level: 'info',
      });
    }
  }

  function cleanup(): void {
    // Disconnect all remote nodes
    for (const node of remoteNodes.values()) {
      node.disconnect();
    }
    remoteNodes.clear();

    // Disconnect shared media
    if (sharedMediaSource) {
      sharedMediaSource.disconnect();
    }
    if (sharedMediaStream) {
      sharedMediaStream.getTracks().forEach(t => t.stop());
    }

    // Disconnect mic source (do NOT stop raw mic tracks — AudioInputDeviceService owns them)
    micSourceNode.disconnect();

    // Close AudioContext (async, swallow errors)
    audioContext.close().catch(() => {});
  }

  return {
    stream: destination.stream,
    cleanup,
    sharedMediaActive,
    swapMicSource,
    connectRemoteTrack,
    disconnectRemoteTrack,
    audioContext,
  };
}
